import { ProductField } from '../types/mockData';
import { TemplateShape } from '../types/dbTypes';
import { Font } from '../types/dbTypes';
import { generateTextPath, Font as TextGenFont } from './textGenerator';
import { escapeXml } from '../utils/xmlUtils';
// Note: weldPaths was removed because clipper polygon-based welding destroys bezier curves.
// SVG uses fill-rule="nonzero" for text paths — TrueType/OpenType fonts are wound for nonzero.

// Re-export escapeXml for backwards compatibility
export { escapeXml };

// Extended field type that includes score/engrave/fontFamily
export interface ExtendedField extends ProductField {
    score?: boolean;
    engrave?: boolean;
    fontFamily?: string;
}

// ============================================================
// TEXT SIZING CONFIGURATION
// ============================================================

// Default font family
export const DEFAULT_FONT_FAMILY = 'Roboto';

// ============================================================
// MEASUREMENT CACHING
// ============================================================

// Cache for SVG text measurements to avoid repeated DOM operations
const svgMeasurementCache = new Map<string, { width: number; height: number; ascent: number; descent: number }>();
const canvasMeasurementCache = new Map<string, number>();
const MAX_CACHE_SIZE = 500;

/**
 * Clear measurement caches (call when fonts change)
 */
export function clearMeasurementCache(): void {
    svgMeasurementCache.clear();
    canvasMeasurementCache.clear();
}

/**
 * Evict oldest entry if cache is full
 */
function evictIfFull<T>(cache: Map<string, T>): void {
    if (cache.size >= MAX_CACHE_SIZE) {
        const firstKey = cache.keys().next().value;
        if (firstKey) cache.delete(firstKey);
    }
}

/**
 * Capitalize the first letter of each word in a string
 * "modernlove" -> "ModernLove"
 * "modern-love" -> "Modern-Love"
 */
function capitalizeWords(str: string): string {
    // Split on word boundaries (spaces, hyphens, underscores) but keep the separators
    return str.replace(/\b\w/g, char => char.toUpperCase());
}

/**
 * Parse font family string into family name and style
 * Handles formats like "modernlove-regular" -> family: "ModernLove", weight: "Regular"
 */
export function parseFontFamily(fontFamily: string): { family: string; style: string | null; weight: string | null } {
    // Common font style/weight patterns
    const stylePatterns = [
        'Regular', 'Bold', 'Italic', 'BoldItalic', 'Light', 'Medium', 'SemiBold',
        'ExtraBold', 'Black', 'Thin', 'Heavy', 'Oblique', 'LightItalic', 'MediumItalic'
    ];

    // Try to match pattern: FontName-Style
    for (const pattern of stylePatterns) {
        const regex = new RegExp(`^(.+?)[-_\\s](${pattern})$`, 'i');
        const match = fontFamily.match(regex);

        if (match) {
            const rawFamily = match[1];
            const rawStyle = match[2];

            // Capitalize the family name (modernlove -> ModernLove)
            const family = capitalizeWords(rawFamily);

            // Determine if it's a style (italic/oblique) or weight
            const isItalic = /italic|oblique/i.test(rawStyle);

            // Capitalize the weight/style properly
            const capitalizedStyle = capitalizeWords(rawStyle);

            return {
                family: family,
                style: isItalic ? 'italic' : null,
                weight: !isItalic ? capitalizedStyle : null
            };
        }
    }

    // No pattern matched, capitalize and return as-is
    return {
        family: capitalizeWords(fontFamily),
        style: null,
        weight: null
    };
}

/**
 * Measure text width using canvas (accurate for any font)
 * This must be accurate because different fonts have vastly different widths
 * Results are cached to avoid repeated canvas operations
 */
export function measureTextWidth(text: string, fontSize: number, fontFamily: string = DEFAULT_FONT_FAMILY): number {
    const cacheKey = `w|${text}|${fontFamily}|${fontSize}`;

    // Check cache first
    const cached = canvasMeasurementCache.get(cacheKey);
    if (cached !== undefined) {
        return cached;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        // Fallback to estimate if canvas not available
        return text.length * fontSize * 0.55;
    }

    // CRITICAL: Use the exact font family provided
    // Quote the font family to handle fonts with spaces (e.g., "Modern Love")
    ctx.font = `${fontSize}px "${fontFamily}"`;

    const metrics = ctx.measureText(text);
    const width = metrics.width;

    // Cache the result
    evictIfFull(canvasMeasurementCache);
    canvasMeasurementCache.set(cacheKey, width);

    return width;
}

/**
 * Measure actual text height using font metrics (critical for different fonts)
 * Different fonts have vastly different vertical proportions at the same point size
 * Results are cached to avoid repeated canvas operations
 */
export function measureTextHeight(text: string, fontSize: number, fontFamily: string = DEFAULT_FONT_FAMILY): number {
    const cacheKey = `h|${text}|${fontFamily}|${fontSize}`;

    // Check cache first
    const cached = canvasMeasurementCache.get(cacheKey);
    if (cached !== undefined) {
        return cached;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        // Fallback to estimate if canvas not available
        return fontSize;
    }

    // CRITICAL: Use the exact font family provided
    ctx.font = `${fontSize}px "${fontFamily}"`;

    const metrics = ctx.measureText(text);

    // Calculate actual height from font metrics
    // actualBoundingBoxAscent: distance from baseline to top of highest character
    // actualBoundingBoxDescent: distance from baseline to bottom of lowest character
    const actualHeight = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;

    // Cache the result
    evictIfFull(canvasMeasurementCache);
    canvasMeasurementCache.set(cacheKey, actualHeight);

    return actualHeight;
}

// ============================================================
// NEW TEXT FITTING SYSTEM - Using SVG getBBox() for accuracy
// ============================================================

/**
 * Measure text dimensions using SVG getBBox()
 * This is more accurate than canvas because it uses the same rendering engine as the final output
 * Results are cached to avoid repeated DOM operations
 */
export function measureTextSvg(text: string, fontFamily: string, fontSize: number): {
    width: number;
    height: number;
    ascent: number;   // distance from baseline to top (positive)
    descent: number;  // distance from baseline to bottom (positive)
} {
    const cacheKey = `${text}|${fontFamily}|${fontSize}`;

    // Check cache first
    const cached = svgMeasurementCache.get(cacheKey);
    if (cached) {
        return cached;
    }

    // Create temporary SVG element
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.style.position = 'absolute';
    svg.style.visibility = 'hidden';
    svg.style.width = '0';
    svg.style.height = '0';

    const textEl = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    textEl.setAttribute('font-family', fontFamily);
    textEl.setAttribute('font-size', String(fontSize));
    textEl.textContent = text;

    svg.appendChild(textEl);
    document.body.appendChild(svg);

    const bbox = textEl.getBBox();
    document.body.removeChild(svg);

    // getBBox() returns:
    // - x: left edge (usually 0 for text)
    // - y: NEGATIVE value = distance from baseline to TOP of text
    // - width: total text width
    // - height: total text height (ascent + descent)
    const ascent = -bbox.y;  // Convert negative to positive
    const descent = bbox.height - ascent;

    const result = {
        width: bbox.width,
        height: bbox.height,
        ascent,
        descent
    };

    // Cache the result
    evictIfFull(svgMeasurementCache);
    svgMeasurementCache.set(cacheKey, result);

    return result;
}

/**
 * Calculate text size and position to fit within a bounding box
 * - Text fills the box as much as possible
 * - Vertically centered
 * - Horizontally centered
 * - Never exceeds box boundaries
 */
export function calculateTextFit(
    text: string,
    fontFamily: string,
    box: { x: number; y: number; width: number; height: number }
): {
    fontSize: number;
    x: number;      // horizontal center position
    y: number;      // baseline Y position
} {
    // Step 1: Measure at reference size for precision
    const refSize = 100;
    const measured = measureTextSvg(text, fontFamily, refSize);

    // Step 2: Calculate scale to fit HEIGHT (with tiny buffer)
    const heightScale = (box.height * 0.99) / measured.height;

    // Step 3: Calculate scale to fit WIDTH (with tiny buffer)
    const widthScale = (box.width * 0.99) / measured.width;

    // Step 4: Use the SMALLER scale (text must fit both dimensions)
    const scale = Math.min(heightScale, widthScale);
    const fontSize = refSize * scale;

    // Step 5: Re-measure at final size for accurate positioning
    const final = measureTextSvg(text, fontFamily, fontSize);

    // Step 6: Position - vertically centered, horizontally centered
    const x = box.x + box.width / 2;  // center X

    // For vertical centering: baseline = box center + (ascent - descent) / 2
    const y = box.y + (box.height + final.ascent - final.descent) / 2;

    return { fontSize, x, y };
}

/**
 * Helper to strip existing styles and inject new fill/stroke attributes
 */
function recolorSvgContent(content: string, fill: string | null, stroke: string | null, strokeWidth: string | null): string {
    // First, strip existing style attributes from ALL elements
    let result = content
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')  // Remove <style> elements
        .replace(/<defs[^>]*>[\s\S]*?<\/defs>/gi, '')    // Remove <defs> (contains style definitions)
        .replace(/\s+fill="[^"]*"/gi, '')                 // Remove fill attributes
        .replace(/\s+stroke="[^"]*"/gi, '')               // Remove stroke attributes
        .replace(/\s+stroke-width="[^"]*"/gi, '')         // Remove stroke-width
        .replace(/\s+stroke-dasharray="[^"]*"/gi, '')     // Remove stroke-dasharray
        .replace(/\s+style="[^"]*"/gi, '')                // Remove inline styles
        .replace(/\s+class="[^"]*"/gi, '');               // Remove class attributes (may reference removed styles)

    // Build attribute string to inject
    const attrs: string[] = [];
    if (fill !== null) attrs.push(`fill="${fill}"`);
    if (stroke !== null) attrs.push(`stroke="${stroke}"`);
    if (strokeWidth !== null) attrs.push(`stroke-width="${strokeWidth}"`);
    const attrStr = attrs.join(' ');

    // Inject attributes into ALL visual elements including <g> groups
    // This ensures nested groups and compound paths get the colors
    result = result.replace(/<(g|path|rect|circle|ellipse|polygon|polyline|line|use)(\s|>)/gi, `<$1 ${attrStr}$2`);

    return result;
}

/**
 * Extract graphic SVG content for preview rendering.
 * Parses the SVG to get inner content and calculate proper transform
 * for positioning and scaling within a shape's bounds.
 * Also applies score/engrave/cut colors based on shape settings.
 *
 * @param svgData - The full SVG string (including <svg> wrapper)
 * @param shape - The shape with bounds and optional color settings
 * @returns Object with innerContent and transform, or null if parsing fails
 */
export function extractGraphicSvgForPreview(
    svgData: string,
    shape: {
        x: number;
        y: number;
        width: number;
        height: number;
        score?: boolean;
        engrave?: boolean;
        cut?: boolean;
        scoreColor?: string;
        engraveColor?: string;
        cutColor?: string;
    }
): { innerContent: string; transform: string } | null {
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgData, 'image/svg+xml');
    const svgElem = svgDoc.querySelector('svg');

    if (!svgElem) {
        return null;
    }

    // Get the viewBox to calculate proper scaling
    let svgViewBoxWidth = shape.width;
    let svgViewBoxHeight = shape.height;
    let svgViewBoxX = 0;
    let svgViewBoxY = 0;

    const viewBox = svgElem.getAttribute('viewBox');
    if (viewBox) {
        const parts = viewBox.split(/[\s,]+/).map(Number);
        if (parts.length === 4) {
            svgViewBoxX = parts[0];
            svgViewBoxY = parts[1];
            svgViewBoxWidth = parts[2];
            svgViewBoxHeight = parts[3];
        }
    } else {
        // Fall back to width/height attributes
        const widthAttr = svgElem.getAttribute('width');
        const heightAttr = svgElem.getAttribute('height');
        if (widthAttr) svgViewBoxWidth = parseFloat(widthAttr.replace(/[^\d.]/g, '')) || svgViewBoxWidth;
        if (heightAttr) svgViewBoxHeight = parseFloat(heightAttr.replace(/[^\d.]/g, '')) || svgViewBoxHeight;
    }

    // Calculate scale to fit graphic into shape bounds
    const scaleX = shape.width / svgViewBoxWidth;
    const scaleY = shape.height / svgViewBoxHeight;
    const scale = Math.min(scaleX, scaleY); // Maintain aspect ratio

    // Center the graphic if aspect ratios don't match
    const scaledWidth = svgViewBoxWidth * scale;
    const scaledHeight = svgViewBoxHeight * scale;
    const offsetX = (shape.width - scaledWidth) / 2;
    const offsetY = (shape.height - scaledHeight) / 2;

    // Build transform: translate to position, then scale, then offset for viewBox origin
    const transform = `translate(${shape.x + offsetX}, ${shape.y + offsetY}) scale(${scale}) translate(${-svgViewBoxX}, ${-svgViewBoxY})`;

    // Get ALL inner content (not the outer <svg> tag)
    let innerContent = svgElem.innerHTML;

    // Apply colors based on score/engrave/cut settings
    const useScore = shape.score ?? false;
    const useEngrave = shape.engrave ?? true;
    const useCut = shape.cut ?? false;

    // Get custom colors (with defaults)
    const scoreColor = shape.scoreColor || '#fe0002';
    const engraveColor = shape.engraveColor || '#000000';
    const cutColor = shape.cutColor || '#00befe';

    // Calculate stroke width, compensating for any internal scale transforms
    let strokeWidth = '0.3';
    const scaleMatch = innerContent.match(/transform="[^"]*scale\(([^,\)]+)/);
    if (scaleMatch) {
        const scaleFactor = parseFloat(scaleMatch[1]);
        if (scaleFactor !== 1) {
            strokeWidth = (0.3 / scaleFactor).toFixed(2);
        }
    }

    // For preview, we combine all enabled operations into a single visual
    // Priority: engrave (fill) + score (stroke overlay)
    if (useEngrave && useScore) {
        // Both engrave and score: show fill with stroke overlay
        innerContent = recolorSvgContent(innerContent, engraveColor, scoreColor, strokeWidth);
    } else if (useEngrave) {
        // Engrave only: fill with no stroke
        innerContent = recolorSvgContent(innerContent, engraveColor, 'none', null);
    } else if (useScore) {
        // Score only: stroke with no fill
        innerContent = recolorSvgContent(innerContent, 'none', scoreColor, strokeWidth);
    } else if (useCut) {
        // Cut only: stroke with cut color
        innerContent = recolorSvgContent(innerContent, 'none', cutColor, strokeWidth);
    }
    // If none are enabled, content stays as-is (shouldn't happen normally)

    return { innerContent, transform };
}

/**
 * Build SVG from template shapes and field values
 * This is the single source of truth for SVG generation
 */
export function buildSvg(
    shapes: TemplateShape[],
    fields: ExtendedField[],
    values: Record<string, string>,
    width: number,
    height: number
): string {
    const svgParts: string[] = [];

    // SVG header
    svgParts.push(`<svg xmlns="http://www.w3.org/2000/svg" width="${width}mm" height="${height}mm" viewBox="0 0 ${width} ${height}">`);

    // Sort shapes - border/full-size shapes first (for layering)
    const sortedShapes = [...shapes].sort((a, b) => {
        const aIsFullSize = Math.abs(a.width - width) < 1 && Math.abs(a.height - height) < 1;
        const bIsFullSize = Math.abs(b.width - width) < 1 && Math.abs(b.height - height) < 1;
        if (aIsFullSize && !bIsFullSize) return -1;
        if (!aIsFullSize && bIsFullSize) return 1;
        return 0;
    });

    // Add all shapes (rectangles and graphics)
    sortedShapes.forEach(shape => {
        // Handle raster images (always engrave operation)
        if (shape.type === 'raster' && shape.rasterData) {
            const hasRotation = shape.rotation && shape.rotation !== 0;
            const centerX = shape.x + shape.width / 2;
            const centerY = shape.y + shape.height / 2;

            // Build transform for rotation if needed
            const transform = hasRotation
                ? `transform="rotate(${shape.rotation} ${centerX} ${centerY})"`
                : '';

            // Raster images are always engraved (no score/cut options)
            const imgElement = `<image
                x="${shape.x}"
                y="${shape.y}"
                width="${shape.width}"
                height="${shape.height}"
                ${transform}
                href="${shape.rasterData}"
                preserveAspectRatio="none"
            />`;
            svgParts.push(imgElement);
            return; // Skip rest of shape processing
        }

        // Handle graphic shapes (SVG monograms, decorative elements)
        if (shape.type === 'graphic' && shape.svgData) {
            // Check score/engrave/cut flags (default: engrave only)
            const useScore = shape.score ?? false;
            const useEngrave = shape.engrave ?? true;
            const useCut = shape.cut ?? false;

            // Skip if none are enabled
            if (!useScore && !useEngrave && !useCut) {
                console.warn(`[buildSvg] Graphic shape has no score, engrave, or cut enabled - skipping`);
                return;
            }

            const hasRotation = shape.rotation && shape.rotation !== 0;
            const centerX = shape.x + shape.width / 2;
            const centerY = shape.y + shape.height / 2;

            // Open rotation group if needed
            if (hasRotation) {
                svgParts.push(`<g transform="rotate(${shape.rotation} ${centerX} ${centerY})">`);
            }

            // Parse the stored SVG to extract content and viewBox
            const parser = new DOMParser();
            const svgDoc = parser.parseFromString(shape.svgData, 'image/svg+xml');
            const svgElem = svgDoc.querySelector('svg');

            if (svgElem) {
                // Get the viewBox to calculate proper scaling
                let svgViewBoxWidth = shape.width;
                let svgViewBoxHeight = shape.height;
                let svgViewBoxX = 0;
                let svgViewBoxY = 0;

                const viewBox = svgElem.getAttribute('viewBox');
                if (viewBox) {
                    const parts = viewBox.split(/[\s,]+/).map(Number);
                    if (parts.length === 4) {
                        svgViewBoxX = parts[0];
                        svgViewBoxY = parts[1];
                        svgViewBoxWidth = parts[2];
                        svgViewBoxHeight = parts[3];
                    }
                } else {
                    // Fall back to width/height attributes
                    const widthAttr = svgElem.getAttribute('width');
                    const heightAttr = svgElem.getAttribute('height');
                    if (widthAttr) svgViewBoxWidth = parseFloat(widthAttr.replace(/[^\d.]/g, '')) || svgViewBoxWidth;
                    if (heightAttr) svgViewBoxHeight = parseFloat(heightAttr.replace(/[^\d.]/g, '')) || svgViewBoxHeight;
                }

                // Calculate scale to fit graphic into shape bounds
                const scaleX = shape.width / svgViewBoxWidth;
                const scaleY = shape.height / svgViewBoxHeight;
                const scale = Math.min(scaleX, scaleY); // Maintain aspect ratio

                // Center the graphic if aspect ratios don't match
                const scaledWidth = svgViewBoxWidth * scale;
                const scaledHeight = svgViewBoxHeight * scale;
                const offsetX = (shape.width - scaledWidth) / 2;
                const offsetY = (shape.height - scaledHeight) / 2;

                // Build transform: translate to position, then scale, then offset for viewBox origin
                const transform = `translate(${shape.x + offsetX}, ${shape.y + offsetY}) scale(${scale}) translate(${-svgViewBoxX}, ${-svgViewBoxY})`;

                // Get ALL inner content (not just first <g>)
                const innerContent = svgElem.innerHTML;

                // Get custom colors (with defaults)
                const scoreColor = shape.scoreColor || '#fe0002';
                const engraveColor = shape.engraveColor || '#000000';
                const cutColor = shape.cutColor || '#00befe';  // Cyan for cut

                // Helper: Strip all styling and inject new fill/stroke attributes directly into elements
                // This is more reliable than CSS because it works on all SVG elements
                const recolorSvg = (content: string, fill: string | null, stroke: string | null, strokeWidth: string | null): string => {
                    // First, strip existing style attributes from ALL elements
                    let result = content
                        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')  // Remove <style> elements
                        .replace(/<defs[^>]*>[\s\S]*?<\/defs>/gi, '')    // Remove <defs> (contains style definitions)
                        .replace(/\s+fill="[^"]*"/gi, '')                 // Remove fill attributes
                        .replace(/\s+stroke="[^"]*"/gi, '')               // Remove stroke attributes
                        .replace(/\s+stroke-width="[^"]*"/gi, '')         // Remove stroke-width
                        .replace(/\s+stroke-dasharray="[^"]*"/gi, '')     // Remove stroke-dasharray
                        .replace(/\s+style="[^"]*"/gi, '')                // Remove inline styles
                        .replace(/\s+class="[^"]*"/gi, '');               // Remove class attributes (may reference removed styles)

                    // Build attribute string to inject
                    const attrs: string[] = [];
                    if (fill !== null) attrs.push(`fill="${fill}"`);
                    if (stroke !== null) attrs.push(`stroke="${stroke}"`);
                    if (strokeWidth !== null) attrs.push(`stroke-width="${strokeWidth}"`);
                    const attrStr = attrs.join(' ');

                    // Inject attributes into ALL visual elements including <g> groups
                    // This ensures nested groups and compound paths get the colors
                    result = result.replace(/<(g|path|rect|circle|ellipse|polygon|polyline|line|use)(\s|>)/gi, `<$1 ${attrStr}$2`);

                    return result;
                };

                // Check if SVG has internal scale transform (stars/polygons from XCS import)
                // If so, we need to compensate the stroke-width to remain visible
                let strokeWidth = '0.3';
                const scaleMatch = innerContent.match(/transform="[^"]*scale\(([^,\)]+)/);
                if (scaleMatch) {
                    const scaleFactor = parseFloat(scaleMatch[1]);
                    if (scaleFactor !== 1) {
                        // Apply inverse compensation so stroke remains visible after scale
                        strokeWidth = (0.3 / scaleFactor).toFixed(2);
                    }
                }

                // Generate SCORE version (stroke, no fill) if enabled
                if (useScore) {
                    const scoreContent = recolorSvg(innerContent, 'none', scoreColor, strokeWidth);
                    svgParts.push(`<g transform="${transform}">`);
                    svgParts.push(scoreContent);
                    svgParts.push('</g>');
                }

                // Generate ENGRAVE version (fill, no stroke) if enabled
                if (useEngrave) {
                    const engraveContent = recolorSvg(innerContent, engraveColor, 'none', null);
                    svgParts.push(`<g transform="${transform}">`);
                    svgParts.push(engraveContent);
                    svgParts.push('</g>');
                }

                // Generate CUT version (stroke, dashed for preview) if enabled
                if (useCut) {
                    const cutContent = recolorSvg(innerContent, 'none', cutColor, strokeWidth)
                        .replace(/<(g|path|rect|circle|ellipse|polygon|polyline|line|use)(\s)/gi, '<$1 stroke-dasharray="2,1"$2');
                    svgParts.push(`<g transform="${transform}">`);
                    svgParts.push(cutContent);
                    svgParts.push('</g>');
                }
            }

            // Close rotation group if opened
            if (hasRotation) {
                svgParts.push('</g>');
            }

            return; // Skip rest of rectangle processing
        }

        // Handle rectangle shapes
        // Check if shape has rotation
        const hasRotation = shape.rotation && shape.rotation !== 0;

        // Calculate rotation center point (center of shape)
        const centerX = shape.x + shape.width / 2;
        const centerY = shape.y + shape.height / 2;

        // Open rotation group if needed
        if (hasRotation) {
            svgParts.push(`<g transform="rotate(${shape.rotation} ${centerX} ${centerY})">`);
        }

        // Add rectangle (stroke only, no fill) - Blue stroke to differentiate from graphics
        svgParts.push(`<rect x="${shape.x}" y="${shape.y}" width="${shape.width}" height="${shape.height}" fill="none" stroke="#0000FF" stroke-width="0.5"/>`);

        // If field is assigned and has a value, add text
        const field = fields.find(f => f.id === shape.fieldId);
        if (field && shape.fieldId) {
            const value = values[shape.fieldId] || '';
            if (value) {
                // Get font for this field (default to DEFAULT_FONT_FAMILY constant)
                const fontFamily = field.fontFamily || DEFAULT_FONT_FAMILY;

                // NEW: Use calculateTextFit() for accurate sizing and positioning
                // This uses SVG getBBox() which matches the final output exactly
                const textFit = calculateTextFit(value, fontFamily, {
                    x: shape.x,
                    y: shape.y,
                    width: shape.width,
                    height: shape.height
                });

                const { fontSize, x: textX, y: textY } = textFit;

                // Determine output type from SHAPE settings (not field) - default to engrave if neither specified
                const useScore = shape.score ?? false;
                const useEngrave = shape.engrave !== false; // Default true if undefined
                const useCut = shape.cut ?? false;

                // Get custom colors from shape (with defaults)
                const scoreColor = shape.scoreColor || '#fe0002';    // xTool Red
                const engraveColor = shape.engraveColor || '#000000'; // xTool Black
                const cutColor = shape.cutColor || '#00befe';         // xTool Cyan

                // Build font attributes for SVG
                const fontAttrs = `font-family="${fontFamily}" font-size="${fontSize}"`;

                // Generate score text (stroke, no fill)
                if (useScore) {
                    svgParts.push(`<text x="${textX}" y="${textY}" ${fontAttrs} fill="none" stroke="${scoreColor}" stroke-width="0.5" text-anchor="middle">${escapeXml(value)}</text>`);
                }

                // Generate engrave text (fill, no stroke)
                if (useEngrave) {
                    svgParts.push(`<text x="${textX}" y="${textY}" ${fontAttrs} fill="${engraveColor}" stroke="none" text-anchor="middle">${escapeXml(value)}</text>`);
                }

                // Generate cut text (dashed stroke for preview)
                if (useCut) {
                    svgParts.push(`<text x="${textX}" y="${textY}" ${fontAttrs} fill="none" stroke="${cutColor}" stroke-width="0.5" stroke-dasharray="2,1" text-anchor="middle">${escapeXml(value)}</text>`);
                }
            }
        }

        // Close rotation group if opened
        if (hasRotation) {
            svgParts.push(`</g>`);
        }
    });

    svgParts.push('</svg>');
    return svgParts.join('\n');
}

/**
 * Build SVG using glyph-based text rendering (async version)
 *
 * This version converts text to vector paths using opentype.js,
 * providing exact measurements and consistent output across all fonts.
 *
 * Benefits:
 * - Exact text sizing and positioning
 * - No font loading timing issues
 * - Works with any font
 * - Identical output to XCS generation
 *
 * @param shapes - Template shapes
 * @param fields - Extended fields with font settings
 * @param values - Field values (text to render)
 * @param width - SVG width in mm
 * @param height - SVG height in mm
 * @param fonts - Available fonts with base64 data for glyph extraction
 */
export async function buildSvgAsync(
    shapes: TemplateShape[],
    fields: ExtendedField[],
    values: Record<string, string>,
    width: number,
    height: number,
    fonts: Font[]
): Promise<string> {
    const svgParts: string[] = [];

    // SVG header
    svgParts.push(`<svg xmlns="http://www.w3.org/2000/svg" width="${width}mm" height="${height}mm" viewBox="0 0 ${width} ${height}">`);

    // Sort shapes - border/full-size shapes first (for layering)
    const sortedShapes = [...shapes].sort((a, b) => {
        const aIsFullSize = Math.abs(a.width - width) < 1 && Math.abs(a.height - height) < 1;
        const bIsFullSize = Math.abs(b.width - width) < 1 && Math.abs(b.height - height) < 1;
        if (aIsFullSize && !bIsFullSize) return -1;
        if (!aIsFullSize && bIsFullSize) return 1;
        return 0;
    });

    // Process shapes (must handle async text generation)
    for (const shape of sortedShapes) {
        // Handle raster images (always engrave operation)
        if (shape.type === 'raster' && shape.rasterData) {
            const hasRotation = shape.rotation && shape.rotation !== 0;
            const centerX = shape.x + shape.width / 2;
            const centerY = shape.y + shape.height / 2;

            // Build transform for rotation if needed
            const transform = hasRotation
                ? `transform="rotate(${shape.rotation} ${centerX} ${centerY})"`
                : '';

            // Raster images are always engraved (no score/cut options)
            const imgElement = `<image
                x="${shape.x}"
                y="${shape.y}"
                width="${shape.width}"
                height="${shape.height}"
                ${transform}
                href="${shape.rasterData}"
                preserveAspectRatio="none"
            />`;
            svgParts.push(imgElement);
            continue;
        }

        // Handle graphic shapes (SVG monograms, decorative elements)
        if (shape.type === 'graphic' && shape.svgData) {
            // Check score/engrave/cut flags (default: engrave only)
            const useScore = shape.score ?? false;
            const useEngrave = shape.engrave ?? true;
            const useCut = shape.cut ?? false;

            // Skip if none are enabled
            if (!useScore && !useEngrave && !useCut) {
                continue;
            }

            const hasRotation = shape.rotation && shape.rotation !== 0;
            const centerX = shape.x + shape.width / 2;
            const centerY = shape.y + shape.height / 2;

            // Open rotation group if needed
            if (hasRotation) {
                svgParts.push(`<g transform="rotate(${shape.rotation} ${centerX} ${centerY})">`);
            }

            // Parse the stored SVG to extract content and viewBox
            const parser = new DOMParser();
            const svgDoc = parser.parseFromString(shape.svgData, 'image/svg+xml');
            const svgElem = svgDoc.querySelector('svg');

            if (svgElem) {
                // Get the viewBox to calculate proper scaling
                let svgViewBoxWidth = shape.width;
                let svgViewBoxHeight = shape.height;
                let svgViewBoxX = 0;
                let svgViewBoxY = 0;

                const viewBox = svgElem.getAttribute('viewBox');
                if (viewBox) {
                    const parts = viewBox.split(/[\s,]+/).map(Number);
                    if (parts.length === 4) {
                        svgViewBoxX = parts[0];
                        svgViewBoxY = parts[1];
                        svgViewBoxWidth = parts[2];
                        svgViewBoxHeight = parts[3];
                    }
                } else {
                    // Fall back to width/height attributes
                    const widthAttr = svgElem.getAttribute('width');
                    const heightAttr = svgElem.getAttribute('height');
                    if (widthAttr) svgViewBoxWidth = parseFloat(widthAttr.replace(/[^\d.]/g, '')) || svgViewBoxWidth;
                    if (heightAttr) svgViewBoxHeight = parseFloat(heightAttr.replace(/[^\d.]/g, '')) || svgViewBoxHeight;
                }

                // Calculate scale to fit graphic into shape bounds
                const scaleX = shape.width / svgViewBoxWidth;
                const scaleY = shape.height / svgViewBoxHeight;
                const scale = Math.min(scaleX, scaleY);

                // Center the graphic if aspect ratios don't match
                const scaledWidth = svgViewBoxWidth * scale;
                const scaledHeight = svgViewBoxHeight * scale;
                const offsetX = (shape.width - scaledWidth) / 2;
                const offsetY = (shape.height - scaledHeight) / 2;

                // Build transform
                const transform = `translate(${shape.x + offsetX}, ${shape.y + offsetY}) scale(${scale}) translate(${-svgViewBoxX}, ${-svgViewBoxY})`;

                // Get inner content
                const innerContent = svgElem.innerHTML;

                // Get custom colors (with defaults)
                const scoreColor = shape.scoreColor || '#fe0002';
                const engraveColor = shape.engraveColor || '#000000';
                const cutColor = shape.cutColor || '#00befe';

                // Helper: Strip styling and inject new attributes
                const recolorSvg = (content: string, fill: string | null, stroke: string | null, strokeWidth: string | null): string => {
                    let result = content
                        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
                        .replace(/<defs[^>]*>[\s\S]*?<\/defs>/gi, '')
                        .replace(/\s+fill="[^"]*"/gi, '')
                        .replace(/\s+stroke="[^"]*"/gi, '')
                        .replace(/\s+stroke-width="[^"]*"/gi, '')
                        .replace(/\s+stroke-dasharray="[^"]*"/gi, '')
                        .replace(/\s+style="[^"]*"/gi, '')
                        .replace(/\s+class="[^"]*"/gi, '');

                    const attrs: string[] = [];
                    if (fill !== null) attrs.push(`fill="${fill}"`);
                    if (stroke !== null) attrs.push(`stroke="${stroke}"`);
                    if (strokeWidth !== null) attrs.push(`stroke-width="${strokeWidth}"`);
                    const attrStr = attrs.join(' ');

                    result = result.replace(/<(g|path|rect|circle|ellipse|polygon|polyline|line|use)(\s|>)/gi, `<$1 ${attrStr}$2`);

                    return result;
                };

                // Check if SVG has internal scale transform (stars/polygons from XCS import)
                // If so, we need to compensate the stroke-width to remain visible
                let strokeWidth = '0.3';
                const scaleMatch = innerContent.match(/transform="[^"]*scale\(([^,\)]+)/);
                if (scaleMatch) {
                    const scaleFactor = parseFloat(scaleMatch[1]);
                    if (scaleFactor !== 1) {
                        // Apply inverse compensation so stroke remains visible after scale
                        strokeWidth = (0.3 / scaleFactor).toFixed(2);
                    }
                }

                // Generate versions based on flags
                if (useScore) {
                    const scoreContent = recolorSvg(innerContent, 'none', scoreColor, strokeWidth);
                    svgParts.push(`<g transform="${transform}">`);
                    svgParts.push(scoreContent);
                    svgParts.push('</g>');
                }

                if (useEngrave) {
                    const engraveContent = recolorSvg(innerContent, engraveColor, 'none', null);
                    svgParts.push(`<g transform="${transform}">`);
                    svgParts.push(engraveContent);
                    svgParts.push('</g>');
                }

                if (useCut) {
                    const cutContent = recolorSvg(innerContent, 'none', cutColor, strokeWidth)
                        .replace(/<(g|path|rect|circle|ellipse|polygon|polyline|line|use)(\s)/gi, '<$1 stroke-dasharray="2,1"$2');
                    svgParts.push(`<g transform="${transform}">`);
                    svgParts.push(cutContent);
                    svgParts.push('</g>');
                }
            }

            // Close rotation group if opened
            if (hasRotation) {
                svgParts.push('</g>');
            }

            continue; // Skip rest of rectangle processing
        }

        // Handle rectangle shapes
        const hasRotation = shape.rotation && shape.rotation !== 0;
        const centerX = shape.x + shape.width / 2;
        const centerY = shape.y + shape.height / 2;

        // Open rotation group if needed
        if (hasRotation) {
            svgParts.push(`<g transform="rotate(${shape.rotation} ${centerX} ${centerY})">`);
        }

        // Add rectangle (stroke only, no fill)
        svgParts.push(`<rect x="${shape.x}" y="${shape.y}" width="${shape.width}" height="${shape.height}" fill="none" stroke="#0000FF" stroke-width="0.5"/>`);

        // If field is assigned and has a value, generate text as path
        const field = fields.find(f => f.id === shape.fieldId);
        if (field && shape.fieldId) {
            const value = values[shape.fieldId] || '';
            if (value) {
                // Get font for this field
                const fontFamily = field.fontFamily || DEFAULT_FONT_FAMILY;

                // Convert fonts to textGenerator format (filter out fonts without URLs)
                const textGenFonts: TextGenFont[] = fonts
                    .filter(f => f.url)
                    .map(f => ({
                        id: f.id,
                        name: f.name,
                        family: f.family,
                        url: f.url!  // Safe: filtered above
                    }));

                // Generate glyph-based text path
                const textResult = await generateTextPath(value, fontFamily, textGenFonts, {
                    x: shape.x,
                    y: shape.y,
                    width: shape.width,
                    height: shape.height
                });

                // Determine output type from SHAPE settings
                const useScore = shape.score ?? false;
                const useEngrave = shape.engrave !== false;
                const useCut = shape.cut ?? false;

                // Get custom colors from shape
                const scoreColor = shape.scoreColor || '#fe0002';
                const engraveColor = shape.engraveColor || '#000000';
                const cutColor = shape.cutColor || '#00befe';

                if (textResult) {
                    // Use nonzero fill rule — TrueType/OpenType font contours are wound for nonzero.
                    // evenodd causes white artifacts where glyph contours overlap internally.
                    const { path, transform } = textResult;

                    // Generate score version (stroke, no fill)
                    if (useScore) {
                        svgParts.push(`<path d="${path}" transform="${transform}" fill="none" stroke="${scoreColor}" stroke-width="0.5" fill-rule="nonzero"/>`);
                    }

                    // Generate engrave version (fill, no stroke)
                    if (useEngrave) {
                        svgParts.push(`<path d="${path}" transform="${transform}" fill="${engraveColor}" stroke="none" fill-rule="nonzero"/>`);
                    }

                    // Generate cut version (dashed stroke)
                    if (useCut) {
                        svgParts.push(`<path d="${path}" transform="${transform}" fill="none" stroke="${cutColor}" stroke-width="0.5" stroke-dasharray="2,1" fill-rule="nonzero"/>`);
                    }
                } else {
                    // Fallback to text element if font not found
                    console.warn(`[buildSvgAsync] Font "${fontFamily}" not found, falling back to text element`);
                    const textFit = calculateTextFit(value, fontFamily, {
                        x: shape.x,
                        y: shape.y,
                        width: shape.width,
                        height: shape.height
                    });

                    const { fontSize, x: textX, y: textY } = textFit;
                    const fontAttrs = `font-family="${fontFamily}" font-size="${fontSize}"`;

                    if (useScore) {
                        svgParts.push(`<text x="${textX}" y="${textY}" ${fontAttrs} fill="none" stroke="${scoreColor}" stroke-width="0.5" text-anchor="middle">${escapeXml(value)}</text>`);
                    }
                    if (useEngrave) {
                        svgParts.push(`<text x="${textX}" y="${textY}" ${fontAttrs} fill="${engraveColor}" stroke="none" text-anchor="middle">${escapeXml(value)}</text>`);
                    }
                    if (useCut) {
                        svgParts.push(`<text x="${textX}" y="${textY}" ${fontAttrs} fill="none" stroke="${cutColor}" stroke-width="0.5" stroke-dasharray="2,1" text-anchor="middle">${escapeXml(value)}</text>`);
                    }
                }
            }
        }

        // Close rotation group if opened
        if (hasRotation) {
            svgParts.push(`</g>`);
        }
    }

    svgParts.push('</svg>');
    return svgParts.join('\n');
}
